import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { theme } from 'styles/theme';
import { isHiGridSystemColumnId } from './core/tanstackGridColumns';

/**
 * 동일 값 세로 병합 — 사용자 on/off + 열 지정
 */
export default function GridMergeColumnsPopup({
  x,
  y,
  columns = [],
  enabled,
  selectedIds = [],
  onChange,
  onClose,
}) {
  const ref = useRef(null);
  const [on, setOn] = useState(Boolean(enabled));
  const [ids, setIds] = useState(() => new Set(selectedIds));

  useEffect(() => {
    setOn(Boolean(enabled));
    setIds(new Set(selectedIds));
  }, [enabled, selectedIds]);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let left = x;
    let top = y;
    if (left + rect.width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - rect.width - 8);
    if (top + rect.height > window.innerHeight - 8) top = Math.max(8, window.innerHeight - rect.height - 8);
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  }, [x, y, columns]);

  const apply = () => {
    onChange?.({ enabled: on, columnIds: [...ids] });
    onClose?.();
  };

  const toggle = (id) => {
    setIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const fields = (columns || []).filter((c) => c.id && !isHiGridSystemColumnId(c.id));

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label="동일 값 병합"
      style={{
        position: 'fixed',
        left: x,
        top: y,
        zIndex: 10050,
        width: 280,
        maxHeight: 420,
        background: theme.colors.white,
        border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.borderRadius?.sm || 4,
        boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
        display: 'flex',
        flexDirection: 'column',
        fontSize: theme.typography.fontSize.sm,
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div style={{ padding: '8px 12px', fontWeight: 700, borderBottom: `1px solid ${theme.colors.border}` }}>
        동일 값 병합
      </div>
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          borderBottom: `1px solid ${theme.colors.borderLight || theme.colors.border}`,
          cursor: 'pointer',
        }}
      >
        <input type="checkbox" checked={on} onChange={(e) => setOn(e.target.checked)} />
        연속된 같은 값 셀 병합
      </label>
      <div style={{ padding: '6px 12px', color: theme.colors.text.secondary, fontSize: theme.typography.fontSize.xs }}>
        금액처럼 우연히 같은 값은 합치지 않는 것이 안전합니다. 부서·과목 등 구분 열을 지정하세요.
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0', minHeight: 80, opacity: on ? 1 : 0.5 }}>
        {fields.map((col) => (
          <label
            key={col.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '5px 12px',
              cursor: on ? 'pointer' : 'default',
              userSelect: 'none',
            }}
          >
            <input
              type="checkbox"
              disabled={!on}
              checked={ids.has(col.id)}
              onChange={() => on && toggle(col.id)}
            />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {col.label}
            </span>
          </label>
        ))}
      </div>
      <div
        style={{
          borderTop: `1px solid ${theme.colors.border}`,
          padding: 8,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 6,
        }}
      >
        <button type="button" onClick={onClose} style={btnSecondary}>
          취소
        </button>
        <button type="button" onClick={apply} style={btnPrimary}>
          확인
        </button>
      </div>
    </div>,
    document.body
  );
}

const btnSecondary = {
  padding: '4px 10px',
  border: `1px solid ${theme.colors.border}`,
  borderRadius: 4,
  background: theme.colors.white,
  cursor: 'pointer',
  fontSize: theme.typography.fontSize.xs,
};

const btnPrimary = {
  padding: '4px 12px',
  border: 'none',
  borderRadius: 4,
  background: theme.colors.primary,
  color: theme.colors.white,
  cursor: 'pointer',
  fontSize: theme.typography.fontSize.xs,
  fontWeight: 600,
};
